import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { formatAppName } from '../lib/appName';
import { formatTimestamp, getAppHistory, type HistoryEvent } from '../lib/history';
import type { Deployment, PlanResult, VerificationResult } from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface HistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  deployments: Deployment[];
  planResults: PlanResult[];
  verificationResults: VerificationResult[];
}

const getEventIcon = (type: HistoryEvent['type'], event: HistoryEvent) => {
  // Determine color based on success/error state
  let colorClass = 'text-gray-600'; // default

  if (type === 'deployment' && event.status) {
    colorClass = event.status === 'success' ? 'text-green-600' : 'text-red-600';
  } else if (type === 'plan') {
    colorClass = event.hasChanges ? 'text-yellow-600' : 'text-green-600';
  } else if (type === 'verification' && event.status) {
    const isSuccess = event.status === 'success' || event.status === 'passed';
    colorClass = isSuccess ? 'text-green-600' : 'text-red-600';
  }

  switch (type) {
    case 'deployment':
      return <RocketLaunchIcon className={`w-5 h-5 ${colorClass}`} />;
    case 'plan':
      return <ClipboardDocumentListIcon className={`w-5 h-5 ${colorClass}`} />;
    case 'verification':
      return <CheckCircleIcon className={`w-5 h-5 ${colorClass}`} />;
  }
};

const getEventTypeLabel = (type: HistoryEvent['type']): string => {
  switch (type) {
    case 'deployment':
      return 'Deployment';
    case 'plan':
      return 'Plan Scan';
    case 'verification':
      return 'Validation';
  }
};

export const HistoryDialog = ({
  open,
  onOpenChange,
  appName,
  deployments,
  planResults,
  verificationResults,
}: HistoryDialogProps) => {
  const history = getAppHistory(appName, deployments, planResults, verificationResults);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>History: {formatAppName(appName)}</DialogTitle>
          <DialogDescription>All deployment events for this application</DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-y-auto space-y-2 pr-2'>
          {history.length === 0 ? (
            <div className='text-center py-8 text-gray-500'>No history events found</div>
          ) : (
            history.map((event, index) => {
              const envColorClass =
                event.environment === 'prod'
                  ? 'bg-green-100 text-green-800'
                  : event.environment === 'dev'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700';

              return (
                <div
                  key={`${event.type}-${event.timestamp}-${index}`}
                  className='border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-start gap-3 flex-1 min-w-0'>
                      <div className='text-xl pt-0.5'>{getEventIcon(event.type, event)}</div>
                      <div className='flex-1 min-w-0'>
                        <div className='font-medium text-gray-900'>
                          {getEventTypeLabel(event.type)}
                        </div>
                        <div className='mt-1 text-sm text-gray-600'>
                          <span>{formatTimestamp(event.timestamp)}</span>
                          <span className='mx-2'>•</span>
                          <span>
                            by {event.createdBy === 'system/api-key' ? 'GitHub' : event.createdBy}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className='flex items-center gap-1.5 flex-shrink-0'>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${envColorClass}`}>
                        {event.environment}
                      </span>
                      {event.version && <CopyableVersionTag version={event.version} />}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className='flex justify-end pt-4 border-t'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
